import type {
  ConversationItem,
  ProvenanceType,
  Speak2MeResumeResponse,
} from '@vivport/backend/types';

export interface NeoHydrationMeta {
  status: 'idle' | 'loading' | 'hydrated' | 'error';
  hydratedNeoChatId?: number;
  errorMessage?: string;
}

export interface NeoDraftState {
  hasText: boolean;
}

export interface NeoConversationState {
  activeProvenanceId?: number;
  activeProvenanceType?: ProvenanceType;
  activeProvenanceCode?: string;
  history: ConversationItem[];
  draft: NeoDraftState;
  hydration: NeoHydrationMeta;
}

export type NeoConversationAction =
  | { type: 'RESET_SESSION' }
  | { type: 'HYDRATION_STARTED' }
  | { type: 'HYDRATE_FROM_RESUME'; response: Speak2MeResumeResponse }
  | { type: 'HYDRATION_FAILED'; message: string }
  | { type: 'APPEND_HISTORY_ITEMS'; items: ConversationItem[] }
  | {
      type: 'SET_ACTIVE_PROVENANCE';
      provenanceId: number;
      provenanceType: ProvenanceType;
      provenanceCode?: string;
    };

const initialAssistantMessage: ConversationItem = {
  id: 'assistant-initial',
  kind: 'assistant_message',
  text: 'What would you like to do?',
};

export function createInitialNeoConversationState(): NeoConversationState {
  return {
    activeProvenanceId: undefined,
    activeProvenanceType: undefined,
    activeProvenanceCode: undefined,
    history: [initialAssistantMessage],
    draft: { hasText: false },
    hydration: { status: 'idle' },
  };
}

function appendItems(state: NeoConversationState, items: ConversationItem[]): NeoConversationState {
  const latestMemberMessage = [...items].reverse().find((item) => item.kind === 'member_message');
  return {
    ...state,
    history: [...state.history, ...items],
    ...(latestMemberMessage
      ? { draft: { hasText: latestMemberMessage.text.trim().length > 0 } }
      : {}),
  };
}

export function neoConversationReducer(
  state: NeoConversationState,
  action: NeoConversationAction
): NeoConversationState {
  if (action.type === 'RESET_SESSION') {
    return createInitialNeoConversationState();
  }
  if (action.type === 'HYDRATION_STARTED') {
    return { ...state, hydration: { status: 'loading' } };
  }
  if (action.type === 'HYDRATE_FROM_RESUME') {
    const snapshot = action.response.snapshot;
    if (!snapshot) {
      return {
        ...createInitialNeoConversationState(),
        hydration: { status: 'hydrated' },
      };
    }
    return {
      activeProvenanceId: snapshot.neoChatId,
      activeProvenanceType: 'NEO_CHAT',
      activeProvenanceCode: state.activeProvenanceCode,
      history: snapshot.conversationItems.length > 0 ? snapshot.conversationItems : [initialAssistantMessage],
      draft: { hasText: false },
      hydration: { status: 'hydrated', hydratedNeoChatId: snapshot.neoChatId },
    };
  }
  if (action.type === 'HYDRATION_FAILED') {
    return {
      ...state,
      hydration: { status: 'error', errorMessage: action.message },
    };
  }
  if (action.type === 'APPEND_HISTORY_ITEMS') {
    return appendItems(state, action.items);
  }
  if (action.type === 'SET_ACTIVE_PROVENANCE') {
    return {
      ...state,
      activeProvenanceId: action.provenanceId,
      activeProvenanceType: action.provenanceType,
      activeProvenanceCode: action.provenanceCode ?? state.activeProvenanceCode,
      hydration:
        action.provenanceType === 'NEO_CHAT'
          ? { status: 'hydrated', hydratedNeoChatId: action.provenanceId }
          : { status: 'hydrated' },
    };
  }
  return state;
}
